import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import { Result, PaginatedResponse } from "../types";

/* ── Helpers ── */
function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function ScoreBadge({ score, size = "md" }: { score: number; size?: "sm" | "md" | "lg" }) {
  const cls =
    score > 140
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
      : score >= 80
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20";
  const sizes = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-3 py-1 text-sm",
    lg: "px-4 py-1.5 text-base",
  };
  return (
    <span className={`inline-block rounded-full font-semibold ${cls} ${sizes[size]}`}>
      {score}
    </span>
  );
}

const YEARS = [2025, 2024, 2023, 2022, 2021];
const ETYPES = [
  { value: "", label: "Barchasi" },
  { value: "Kunduzgi", label: "Kunduzgi" },
  { value: "Sirtqi", label: "Sirtqi" },
  { value: "Kechki", label: "Kechki" },
  { value: "Masofaviy", label: "Masofaviy" },
];

const selectCls = "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow text-sm";
const labelCls = "block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5";

export default function EligibilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  /* ── State ── */
  const [score, setScore] = useState(searchParams.get("score") || "");
  const [ballType, setBallType] = useState(searchParams.get("ball_type") || "kontrakt");
  const [year, setYear] = useState(Number(searchParams.get("year")) || 2025);
  const [selectedSubjects, setSelectedSubjects] = useState(searchParams.get("subjects") || "");
  const [region, setRegion] = useState(searchParams.get("region") || "");
  const [etype, setEtype] = useState(searchParams.get("etype") || "");
  const [language, setLanguage] = useState(searchParams.get("language") || "");
  const [page, setPage] = useState(1);

  const [subjects, setSubjects] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [results, setResults] = useState<PaginatedResponse<Result> | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /* ── Load filter options ── */
  useEffect(() => {
    api.get("/filters/subjects", { params: { year } }).then((r) => setSubjects(r.data || [])).catch(() => {});
    api.get("/filters/regions").then((r) => setRegions(r.data || [])).catch(() => {});
    api.get("/filters/languages").then((r) => setLanguages(r.data || [])).catch(() => {});
  }, [year]);

  /* ── Search ── */
  const doSearch = useCallback(
    (p: number) => {
      if (!score) return;
      setLoading(true);
      setSearched(true);

      const params: Record<string, string | number> = {
        score: Number(score),
        ball_type: ballType,
        year,
        page: p,
        size: 20,
      };
      if (selectedSubjects) params.subjects = selectedSubjects;
      if (region) params.region = region;
      if (etype) params.etype = etype;
      if (language) params.language = language;

      const sp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => sp.set(k, String(v)));
      setSearchParams(sp, { replace: true });

      api
        .get("/eligibility", { params })
        .then((r) => {
          setResults(r.data);
          setPage(p);
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    },
    [score, ballType, year, selectedSubjects, region, etype, language, setSearchParams]
  );

  useEffect(() => {
    if (searchParams.get("score")) {
      doSearch(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMobileFiltersOpen(false);
    doSearch(1);
  };

  const handleReset = () => {
    setScore("");
    setBallType("kontrakt");
    setYear(2025);
    setSelectedSubjects("");
    setRegion("");
    setEtype("");
    setLanguage("");
    setResults(null);
    setSearched(false);
    setSearchParams({});
  };

  /* ── Filters panel (shared between desktop sidebar and mobile drawer) ── */
  const FiltersContent = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Score input */}
      <div>
        <label className={labelCls}>Ball</label>
        <div className="relative">
          <input
            type="number"
            min={0}
            max={256}
            value={score}
            onChange={(e) => setScore(e.target.value)}
            placeholder="Masalan: 150"
            className={selectCls + " pr-12"}
            required
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">/ 256</span>
        </div>
        {/* Score slider */}
        <input
          type="range"
          min={0}
          max={256}
          value={Number(score) || 0}
          onChange={(e) => setScore(e.target.value)}
          className="w-full mt-2 accent-indigo-600 h-1.5"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
          <span>0</span>
          <span>128</span>
          <span>256</span>
        </div>
      </div>

      {/* Ball type toggle */}
      <div>
        <label className={labelCls}>Ball turi</label>
        <div className="flex bg-slate-100 rounded-xl p-1">
          {[
            { value: "kontrakt", label: "Kontrakt" },
            { value: "grand", label: "Grant" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setBallType(opt.value)}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                ballType === opt.value
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Year tabs */}
      <div>
        <label className={labelCls}>Yil</label>
        <div className="flex gap-1">
          {YEARS.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setYear(y)}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${
                year === y
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Subjects */}
      <div>
        <label className={labelCls}>
          Fanlar majmuasi
          <span className="ml-1 text-indigo-500 normal-case tracking-normal font-medium">(asosiy)</span>
        </label>
        <select value={selectedSubjects} onChange={(e) => setSelectedSubjects(e.target.value)} className={selectCls}>
          <option value="">Barchasi</option>
          {subjects.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Region */}
      <div>
        <label className={labelCls}>Hudud</label>
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectCls}>
          <option value="">Barcha hududlar</option>
          {regions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Etype */}
      <div>
        <label className={labelCls}>Ta'lim shakli</label>
        <select value={etype} onChange={(e) => setEtype(e.target.value)} className={selectCls}>
          {ETYPES.map((et) => (
            <option key={et.value} value={et.value}>{et.label}</option>
          ))}
        </select>
      </div>

      {/* Language */}
      <div>
        <label className={labelCls}>Ta'lim tili</label>
        <select value={language} onChange={(e) => setLanguage(e.target.value)} className={selectCls}>
          <option value="">Barcha tillar</option>
          {languages.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          className="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-semibold py-3 rounded-xl transition-all shadow-sm shadow-indigo-200"
        >
          Qidirish
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </form>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
          Ball yetadigan yo'nalishlar
        </h1>
        <p className="text-slate-500">
          Balingizni kiriting va sizga mos yo'nalishlarni toping
        </p>
      </div>

      {/* Mobile filter button */}
      <button
        onClick={() => setMobileFiltersOpen(true)}
        className="lg:hidden w-full flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-6 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
      >
        <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        Filtrlar
        {searched && results ? (
          <span className="bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5 text-xs font-bold">{results.total}</span>
        ) : null}
      </button>

      {/* Mobile filter drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          mobileFiltersOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
        <div
          className={`absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${
            mobileFiltersOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-bold text-lg text-slate-900">Filtrlar</h2>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-4">
            {FiltersContent}
          </div>
        </div>
      </div>

      {/* Layout: sidebar + results */}
      <div className="flex gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtrlar
            </h2>
            {FiltersContent}
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <Spinner />
          ) : results && results.items.length > 0 ? (
            <>
              {/* Results header */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600">
                  Jami <span className="font-bold text-slate-900">{results.total}</span> ta natija topildi
                </p>
              </div>

              {/* Result cards */}
              <div className="space-y-3">
                {results.items.map((item) => (
                  <div
                    key={`${item.id}-${item.direction_id}`}
                    className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md hover:border-slate-200 transition-all p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Left: info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 text-base leading-snug mb-1">
                          {item.direction_name}
                        </h3>
                        <p className="text-sm text-slate-500 mb-3 truncate">{item.institute_name}</p>

                        <div className="flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 rounded-lg px-2.5 py-1 text-xs font-medium">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {item.region_name}
                          </span>
                          <span className="bg-indigo-50 text-indigo-700 rounded-lg px-2.5 py-1 text-xs font-medium">
                            {item.etype}
                          </span>
                          <span className="bg-slate-100 text-slate-600 rounded-lg px-2.5 py-1 text-xs font-medium">
                            {item.language}
                          </span>
                          <span className="bg-slate-100 text-slate-600 rounded-lg px-2.5 py-1 text-xs font-medium">
                            {item.year}
                          </span>
                        </div>
                      </div>

                      {/* Right: scores */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2 flex-shrink-0">
                        <div className="text-center sm:text-right">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                            {ballType === "kontrakt" ? "Kontrakt" : "Grant"}
                          </p>
                          <ScoreBadge score={ballType === "kontrakt" ? item.kontrakt_ball : item.grand_ball} size="lg" />
                        </div>
                        <div className="text-center sm:text-right">
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                            {ballType === "kontrakt" ? "Grant" : "Kontrakt"}
                          </p>
                          <ScoreBadge score={ballType === "kontrakt" ? item.grand_ball : item.kontrakt_ball} size="sm" />
                        </div>
                      </div>
                    </div>

                    {/* Quota info */}
                    <div className="mt-3 pt-3 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
                      <span>Grant kvota: <span className="font-semibold text-slate-700">{item.grand_kvota}</span></span>
                      <span>Kontrakt kvota: <span className="font-semibold text-slate-700">{item.kontrakt_kvota}</span></span>
                      {item.olimp > 0 && (
                        <span>Olimpiadachilar: <span className="font-semibold text-slate-700">{item.olimp}</span></span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {results.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <button
                    disabled={page <= 1}
                    onClick={() => doSearch(page - 1)}
                    className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Oldingi
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(results.pages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (results.pages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= results.pages - 2) {
                        pageNum = results.pages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => doSearch(pageNum)}
                          className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${
                            page === pageNum
                              ? "bg-indigo-600 text-white shadow-sm"
                              : "text-slate-600 hover:bg-slate-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    disabled={page >= results.pages}
                    onClick={() => doSearch(page + 1)}
                    className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                  >
                    Keyingi
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </>
          ) : searched && !loading ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-slate-700 text-lg font-semibold mb-1">Natija topilmadi</p>
              <p className="text-slate-400 text-sm">
                Boshqa parametrlar bilan qaytadan urinib ko'ring
              </p>
            </div>
          ) : !searched ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-slate-700 text-lg font-semibold mb-1">Balingizni kiriting</p>
              <p className="text-slate-400 text-sm">
                {window.innerWidth >= 1024
                  ? "Chap tarafdagi filtrlardan foydalaning"
                  : "Filtrlar tugmasini bosing va balingizni kiriting"}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
